// Config and hooks for the `markdown` sanitize preset in `@github-ui/sanitize`.
// Selected via `sanitize(html, {preset: 'markdown'})`; use it to sanitize rendered
// GitHub-Flavored Markdown (block content).
//
// The config is default-deny (from `SHARED_CONFIG`) plus the GitHub custom-element
// tags and attributes rendered markdown needs, with always-on link hardening applied
// through `addMarkdownSanitizeHooks`. Output is minted through the dedicated
// `github-sanitize-markdown` Trusted Types policy (see `trusted-types.ts`); a strict
// preset never reuses the lax `github-sanitize-lax` policy.

import type {Config, Purifier} from '../types'
import {sanitizeNewWindowLinks, sanitizeNonGitHubImageLinks, SHARED_CONFIG} from './shared-hooks'

// Markdown config — the GitHub custom-element tag baseline rendered markdown needs,
// layered on the shared default-deny base.
export const MARKDOWN_SANITIZE_CONFIG: Readonly<Config> = {
  ...SHARED_CONFIG,

  ADD_TAGS: [
    'action-menu',
    'anchored-position',
    'auto-complete',
    'focus-group',
    'g-emoji',
    'tracking-block',
    'tracking-block-omnibar',
    'tasklist-block-title',
    'details-menu',
    // GH math expressions
    'math-renderer',
    // classic task lists support
    'task-lists',
    // used by unfurled labels
    'tool-tip',
    'turbo-frame',
    'modal-dialog',
    // Used by tasklist-block-title
    'primer-text-field',
  ],
  // Non-`data-*` attributes the custom elements above need to function.
  ADD_ATTR: [
    // attributes for g-emoji components
    'alias',
    'anchor',
    'anchor-offset',
    'fallback-src',
  ],
  // Extra strictness on the rendered path: no inline style, no `hidden`, no `id`.
  FORBID_ATTR: ['style', 'hidden', 'id'],
}

/**
 * Attach the markdown preset's security hooks to a purifier instance: the always-on
 * link and image hardening. Isomorphic — the hooks guard on browser globals so the
 * server (linkedom) path is a safe no-op. Called from each entry's local
 * `createPurifier`.
 */
export function addMarkdownSanitizeHooks(instance: Purifier): void {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('afterSanitizeElements', sanitizeNonGitHubImageLinks)
  // TODO(github/react-platform#1728): add the `js-*` class denylist hook, e.g.
  //   instance.addHook('uponSanitizeAttribute', stripJsHookClasses)
}
